'use client'

import * as React from 'react'
import { FileQuestion } from 'lucide-react'

import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { SkillWeightSummary } from '../lib/get-skill-weights'
import { getSkillWeights } from '../lib/get-skill-weights'
import { LessonModuleCard } from './cards/lesson-module-card'
import { ContentStats } from './content-stats'
import { Tag } from './tag'

export interface LessonModuleModalData {
  id: string
  title: string
  learningFocus: string
  coverUrl: string | null
  estimatedTimeMinutes: number | null
  grade: { id: string; name: string; iconUrl?: string | null } | null
  curricula: { curriculumId: string; curriculum: { name: string; iconUrl?: string | null } }[]
  contributors: {
    contributorId: string
    contributor: { name: string; avatarUrl?: string | null; iconUrl?: string | null }
  }[]
  lessons: {
    lesson: {
      lessonToSkillWeights: {
        skillWeightId: string
        value: number
        skillWeight: { name: string; iconUrl: string | null }
      }[]
    }
  }[]
}

interface LessonModuleBodyProps {
  data: LessonModuleModalData
  /** Optional popularity row. Tags stay non-clickable when `onTagClick` is omitted. */
  stats?: { likes?: number | null; views?: number | null }
  onTagClick?: (type: 'grade' | 'curriculum' | 'contributor', id: string) => void
}

/** Presentational two-column lesson-module detail — cover + XP on the left, title/meta/focus/tags on the right.
 * Shared between the lesson-module modal and the public `/lesson-module` showcase page. */
export function LessonModuleBody({ data, stats, onTagClick }: LessonModuleBodyProps) {
  const skillWeights = React.useMemo(() => getSkillWeights(data.lessons), [data.lessons])

  const grade = data.grade
  const gradeName = grade
    ? grade.name.toLowerCase().startsWith('kelas')
      ? grade.name.charAt(0).toUpperCase() + grade.name.slice(1).toLowerCase()
      : grade.name
    : null

  const metaLine = [gradeName, data.estimatedTimeMinutes ? `~${data.estimatedTimeMinutes} menit` : null]
    .filter(Boolean)
    .join(' • ')

  return (
    <div className='srd:flex srd:flex-col srd:md:flex-row'>
      {/* Left column: cover + XP */}
      <div className='srd:flex srd:flex-col srd:gap-y-4 srd:p-6 srd:md:shrink-0 srd:md:max-w-62 srd:max-w-75 srd:mx-auto'>
        <div className='srd:w-full'>
          <LessonModuleCard id={data.id} coverUrl={data.coverUrl} className='srd:w-full srd:h-auto srd:aspect-4/3' />
        </div>

        <XPSection skillWeights={skillWeights} />
      </div>

      {/* Right column: title + meta + description + tags */}
      <div className='srd:flex srd:flex-col srd:gap-y-2 srd:p-6 srd:flex-1 srd:min-w-0 srd:md:pt-6 srd:pt-0'>
        <h2 className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-black')}>{data.title}</h2>

        {metaLine && (
          <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>{metaLine}</p>
        )}

        {stats && <ContentStats likes={stats.likes} views={stats.views} className='srd:text-neutral-black' />}

        {data.learningFocus && (
          <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>
            {data.learningFocus}
          </p>
        )}

        {onTagClick && (
          <div className='srd:flex srd:flex-wrap srd:gap-x-2 srd:gap-y-1.5 srd:mt-1'>
            {grade && (
              <Tag
                label={gradeName ?? grade.name}
                onClick={() => onTagClick('grade', grade.id)}
                iconUrl={grade.iconUrl ?? undefined}
              />
            )}
            {data.curricula.map((c) => (
              <Tag
                key={c.curriculumId}
                label={c.curriculum.name}
                onClick={() => onTagClick('curriculum', c.curriculumId)}
                iconUrl={c.curriculum.iconUrl ?? undefined}
              />
            ))}
            {data.contributors.map((c) => (
              <Tag
                key={c.contributorId}
                label={c.contributor.name}
                onClick={() => onTagClick('contributor', c.contributorId)}
                iconUrl={c.contributor.iconUrl ?? c.contributor.avatarUrl ?? undefined}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface XPSectionProps {
  skillWeights: SkillWeightSummary[]
}

function XPSection({ skillWeights }: XPSectionProps) {
  return (
    <div className='srd:flex srd:flex-col srd:gap-y-2'>
      <div className='srd:flex srd:flex-col srd:gap-y-1 srd:w-full'>
        {skillWeights.map((weight) => (
          <div key={weight.name} className='srd:w-full srd:flex srd:items-center srd:justify-between srd:gap-x-4'>
            <div className='srd:flex srd:items-start srd:flex-1'>
              {weight.iconUrl ? (
                <div className='srd:size-6 srd:rounded-md srd:overflow-clip srd:mr-2 srd:shrink-0'>
                  <img
                    src={weight.iconUrl}
                    alt={weight.name}
                    className='srd:size-6 srd:object-cover'
                    draggable={false}
                    width={24}
                    height={24}
                  />
                </div>
              ) : (
                <div className='srd:size-6 srd:rounded-md srd:overflow-clip srd:bg-grey-20 srd:flex srd:items-center srd:justify-center srd:mr-2'>
                  <FileQuestion className='srd:size-6 srd:text-grey-60' />
                </div>
              )}
              <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:flex-1')}>{weight.name}</p>
            </div>
            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:font-bold')}>
              +{Math.round(weight.totalWeight)}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
